import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { deleteUserProfile } from "@app/services/users/deleteUserProfileService";
import getRolesByTypeService from "@app/services/users/getRolesByTypeService";
import resendInvitationService from "@app/services/users/resendInvitationService";
import Container from "@components/Container";
import DataTable from "@components/DataTableV2/DataTable";
import ErrorDialog from "@components/ErrorDialog";
import ProgressSpinner from "@components/ProgressSpinner";
import PromptDialog from "@components/PromptDialog";

import useAuthorization from "@hooks/useAuthorization";

import DeleteUser from "@pages/admin/users/Delete";

import { AdminPermissions } from "@src/enum/Permissions";

import {
  getUserRoleTranslationKey,
  formatDateAndTime,
  fetchUserStatus,
} from "@utils/utils";

import { USERS, CUSTOMER_USER } from "@utils/constant";
import { USER_TYPE } from "@utils/enum";

import UserDialog from "../../users/components/UserDialog";

const searchableColumns = ["id", "firstname", "lastname", "email"];

const Users = ({ customerId }) => {
  const { hasAllPermissions } = useAuthorization();
  const { messages } = useIntl();

  const [selectedData, setSelectedData] = useState("");
  const [roleFilters, setRoleFilters] = useState([]);
  const [reloadList, setReloadList] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showResendInvitationModal, setShowResendInvitationModal] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const hasUsersWritePermission = hasAllPermissions([
    AdminPermissions.AdminWriteCustomerUsers,
  ]);

  const handleDeleteUser = async () => {
    const { id: customerId = "" } = selectedData;

    try {
      setIsLoading(true);

      await deleteUserProfile({ id: customerId });

      handleDeleteModal();
    } catch (e) {
      setErrorMessage(messages.exception_error_message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvitation = async () => {
    const { id = "" } = selectedData;

    try {
      setIsLoading(true);

      await resendInvitationService(id);

      handleResendInvitationModal();
    } catch (error) {
      setErrorMessage(messages.exception_error_message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadRulesData = async () => {
      const response = await getRolesByTypeService(CUSTOMER_USER);
      const { data: rules = [] } = response;
      const formattedData = rules.map(o => ({
        name: messages[getUserRoleTranslationKey(o.name)],
        code: o?.id,
      }));
      setRoleFilters(formattedData);
    };

    loadRulesData();
  }, [messages]);

  const handleAddUser = () => {
    if (showAddUserDialog) {
      setReloadList(!reloadList);
    }

    setShowAddUserDialog(!showAddUserDialog);
  };

  const handleDeleteModal = (data = {}) => {
    setSelectedData(data);

    if (showDeleteModal) {
      setReloadList(!reloadList);
    }

    setShowDeleteModal(!showDeleteModal);
  };

  const handleEditUserModal = (data = {}) => {
    setSelectedData(data);

    if (showEditUserModal) {
      setReloadList(!reloadList);
    }

    setShowEditUserModal(!showEditUserModal);
  };

  const handleResendInvitationModal = (data = {}) => {
    setSelectedData(data);

    if (showResendInvitationModal) {
      setReloadList(!reloadList);
    }

    setShowResendInvitationModal(!showResendInvitationModal);
  };

  const verifyUserIsInvitedBefore = data => !!data?.email_verified_at;

  const handleCloseErrorDialog = () => {
    setErrorMessage("");
  };

  const handleCloseResendInvitationModal = () => {
    setShowResendInvitationModal(false);
    setSelectedData({});
  };

  const dataTableConfig = {
    header: {
      title: "title_users",
      actions: [
        {
          type: "button",
          label: messages.label_invite_sub_user,
          icon: "plus",
          id: "create-user",
          onClick: handleAddUser,
          isHidden: !hasUsersWritePermission,
        },
      ],
      dataTableActions: [
        {
          id: "filter",
          type: "button-tooltip",
          icon: "filter",
          variant: "header",
          borderRadius: 0,
          content: messages.filter_description,
        },
      ],
    },
    enable_filter: true,
    filters: [
      {
        title: messages.menu_roles,
        id: "filter[roles.id]",
        type: "multiselect",
        options: [
          {
            name: "filter[roles.id]",
            value: "",
            placeholder: messages.watchlist_label_choose,
            id: "filter[roles.id]",
            options: roleFilters,
            type: "multiselect",
          },
        ],
      },
    ],
    backend_querying: true,
    no_records_message: "datatable_admin_customer_users_no_objects_found",
    enable_search: true,
    enable_query_builder: true,
    enable_csv_download: true,
    csv_filename: `anvandare.csv`,
    api: {
      resource: `/${USERS}`,
      method: "GET",
      search_fields: searchableColumns,
      params: [
        ["include", ["roles", "roles.permissions", "permissions"]],
        ["filter[customer.id]", customerId],
      ],
    },
    columns: [
      {
        title: "label_user_name",
        db_field: "name",
        type: "text",
        sortable: true,
        sortField: "firstname",
      },
      {
        title: "label_email",
        db_field: "email",
        type: "text",
      },
      {
        title: "label_role",
        db_field: "roles",
        type: "text",
        sortable: true,
        sortField: "roles.name",
        formatter: data => {
          const [values] = data;

          return values?.name
            ? String(messages[getUserRoleTranslationKey(values?.name)])
            : "-";
        },
      },
      {
        title: "label_status",
        db_field: "email_verified_at",
        type: "text",
        formatter: fetchUserStatus,
        sortable: true,
      },
      {
        title: "last_login_label",
        db_field: "last_login_at",
        type: "text",
        formatter: value => (value ? formatDateAndTime(value) : "-"),
        sortable: true,
      },
      {
        type: "actions",
        width: "50px",
        isHidden: !hasUsersWritePermission,
        actions: [
          {
            label: messages.label_edit,
            icon: "icon-pen",
            type: "button",
            onClick: handleEditUserModal,
          },
          {
            label: messages.label_resend_verification,
            icon: "icon-pen",
            type: "button",
            onClick: handleResendInvitationModal,
            shouldHideAction: verifyUserIsInvitedBefore,
          },
          {
            label: messages.label_delete,
            icon: "icon-rubber",
            onClick: handleDeleteModal,
            type: "button",
          },
        ],
      },
    ],
  };

  return (
    <Container m={"0px !important"}>
      {isLoading && <ProgressSpinner />}

      {errorMessage && (
        <ErrorDialog
          errorMessage={errorMessage}
          onHide={handleCloseErrorDialog}
          onConfirm={handleCloseErrorDialog}
        />
      )}

      {showAddUserDialog && (
        <UserDialog
          title={messages.label_invite_sub_user}
          userType={USER_TYPE.CUSTOMER}
          customerId={customerId}
          handleClose={handleAddUser}
        />
      )}

      {showEditUserModal && (
        <UserDialog
          title={messages.label_edit_user}
          userType={USER_TYPE.CUSTOMER}
          userDetails={selectedData}
          handleClose={handleEditUserModal}
        />
      )}

      {showResendInvitationModal && (
        <PromptDialog
          title={messages.title_user_not_confirmed_invitation}
          message={`${messages.title_new_user_invite_sent} ${selectedData?.email}`}
          onConfirm={handleResendInvitation}
          onCancel={handleCloseResendInvitationModal}
        />
      )}

      {showDeleteModal && (
        <DeleteUser
          subUser
          onDelete={handleDeleteUser}
          onCancel={handleDeleteModal}
        />
      )}

      {roleFilters.length > 0 && (
        <DataTable config={dataTableConfig} resetData={reloadList} />
      )}
    </Container>
  );
};

Users.propTypes = {
  customerId: PropTypes.string,
};
export default Users;
